import React, { useState, useEffect } from 'react';
import { 
    Box, Paper, Typography, TextField, Button, 
    List, ListItem, ListItemAvatar, Avatar, ListItemText, 
    Divider, CircularProgress, Chip, IconButton 
} from '@mui/material';
import { Send, Person, SmartToy, Email, Sms, Note } from '@mui/icons-material';
import api from '../../../api/axios';
import { parseUtc } from '../../../utils/time';


const MessageItem = ({ msg }) => {
    const isSystem = msg.sender_type === 'system' || msg.sender_type === 'ai';
    const isMe = msg.direction === 'Outbound'; // Simplified logic
    
    let Icon = Person;
    if (msg.channel === 'Email') Icon = Email;
    if (msg.channel === 'SMS') Icon = Sms;
    if (msg.channel === 'Internal') Icon = Note;
    if (msg.sender_type === 'ai') Icon = SmartToy;

    return (
        <ListItem alignItems="flex-start" sx={{ mb: 1 }}>
            <ListItemAvatar>
                <Avatar sx={{ bgcolor: isSystem ? 'grey.300' : 'primary.main' }}>
                    <Icon fontSize="small" />
                </Avatar>
            </ListItemAvatar>
            <ListItemText
                primary={
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                            {msg.sender_name || msg.sender_type}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {parseUtc(msg.created_at).toLocaleString()}
                        </Typography>
                    </Box>
                }
                secondary={
                    <Box>
                        {msg.subject && (
                            <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 0.5 }}>
                                Re: {msg.subject}
                            </Typography>
                        )}
                        <Typography variant="body2" color="text.primary" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                            {msg.content}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                            <Chip label={msg.channel} size="small" variant="outlined" sx={{ mr: 1, fontSize: '0.7rem' }} />
                            {msg.status && <Chip label={msg.status} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />}
                        </Box>
                    </Box>
                }
                sx={{ 
                    bgcolor: 'white', 
                    p: 2, 
                    borderRadius: 3, 
                    border: '1px solid #E2E8F0',
                    ml: 1
                }}
            />
        </ListItem>
    );
};

const JobTimeline = ({ jobId }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [channel, setChannel] = useState('Internal'); // Internal, Email, SMS

    useEffect(() => {
        loadMessages();
    }, [jobId]);

    const loadMessages = async () => {
        try {
            const res = await api.get(`/admin/jobs/${jobId}/messages`);
            setMessages(res.data);
        } catch (err) {
            console.error(err);
        }
        setLoading(false);
    };

    const handleSend = async () => {
        if (!newMessage.trim()) return;
        setSending(true);
        try {
            await api.post(`/admin/jobs/${jobId}/messages`, {
                content: newMessage,
                channel: channel,
                direction: 'Outbound',
                sender_type: 'manager',
                subject: channel === 'Email' ? 'Update regarding your job' : null
            });
            setNewMessage('');
            loadMessages();
        } catch (err) {
            console.error(err);
            alert('Failed to send message');
        }
        setSending(false);
    };

    if (loading) return <CircularProgress />;

    return (
        <Box>
            <Paper elevation={0} sx={{ p: 3, border: '1px solid #E2E8F0', borderRadius: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <Chip 
                        label="Internal Note" 
                        icon={<Note fontSize="small"/>}
                        color={channel === 'Internal' ? 'primary' : 'default'} 
                        onClick={() => setChannel('Internal')}
                    />
                    <Chip 
                        label="Email Client" 
                        icon={<Email fontSize="small"/>}
                        color={channel === 'Email' ? 'primary' : 'default'} 
                        onClick={() => setChannel('Email')}
                    />
                    <Chip 
                        label="SMS Client" 
                        icon={<Sms fontSize="small"/>}
                        color={channel === 'SMS' ? 'primary' : 'default'} 
                        onClick={() => setChannel('SMS')}
                    />
                </Box>
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder={`Type a ${channel.toLowerCase()}...`}
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button 
                        variant="contained" 
                        startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
                        onClick={handleSend}
                        disabled={!newMessage.trim() || sending}
                    >
                        Send
                    </Button>
                </Box>
            </Paper>

            <List>
                {messages.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={4}>
                        No history yet. Start the conversation!
                    </Typography>
                ) : (
                    messages.map((msg) => (
                        <MessageItem key={msg.id} msg={msg} />
                    ))
                )}
            </List>
        </Box>
    );
};

export default JobTimeline;
